import { Injectable } from '@angular/core';
import { OrigamiResponse, ServiceBase } from './base/service.base';
import { Observable } from 'rxjs';
import { SimpleTopic, TopicField } from '@app/origami-api/utils/topic-utils';
import { AgencySubmissionModels } from '@app/pages/main/pages/startup-agency-submission/models/agency-submission.models';
import { ErrorCode } from '@app/origami-api/constants/error-codes';
import { PlaceholderServiceOptions } from '@app/origami-api/services/required-document.service';
import CreatedAgencySubmissionPackage = AgencySubmissionModels.CreatedAgencySubmissionPackage;
import GetPlaceholdersData = PlaceholderServiceOptions.GetPlaceholdersData;

export enum SubmissionProfileStatus {
  Pending = 1,
  Submitted = 2,
  DeficienciesFound = 3,
  Approved = 4,
  ApprovedWithConditions = 5,
  DeemedApproved = 6,
  Withdrawn = 7,
  Rejected = 8,
  Deferred = 9,
  Disapproved = 10,
}

export enum SubmissionProfileSiteStatus {
  Unknown = 0, // Invalid value. For internal use
  Pending = 1,
  ApprovalReceived = 2,
  Rejected = 3,
}

export enum SubmissionProfileViewMode {
  ByCountry = 1,
  BySubmissionProfileIds = 2,
  ByIrb = 4,
}

export enum SubmissionType {
  All = -1,
  Country = 0,
  Region = 1,
}

export interface IGetSubmissionProfiles {
  siteId: number;
  viewMode?: number; // SubmissionProfileViewMode
  viewFilter?: string | number;
  start?: number;
  limit?: number;
  orderByClause?: string;
  searchQuery?: string;
  submissionfilter?: {
    Country: any;
  };

  searchClause?: string; // unused ?
  ftsQuery?: string; // unused ?
}

export interface IGetSubmissionProfile {
  siteId: number;
  submissionProfileId: number;
}

export interface SubmissionProfileHistoryRecord {
  CreatedDateString: string;
  EntityId: number;
  EntityName: string;
  RecordId: number;
  UserActivityLogId: number;
  UserActivityTypeId: PackagesUpdatesActivity;
  UserId: number;
  UserName: string;

  CreatedDate: string;
  Details: SubmissionProfileHistoryRecordDetail[];
}

export interface SubmissionProfileHistoryRecordDetail {
  UserActivityLogId: number;
  PropertyName: string;
  Before: string;
  After: string;
}

export interface GetSubmissionProfileHistoryRecords {
  siteId: number;
  submissionProfileIds: [number];
  userActivityLogId?: number;
}

export interface GetSubmissionProfiles {
  PageNumber: number;
  Records: GetSubmissionProfilesRecord[];
  TotalRowCount: number;
}

export interface LinkedDocument {
  CreatedBy: number;
  CreatedDate: string;
  LockedBy: string;
  ParentId: number;
  SiteId: number;
  TopicFields: TopicField[];
  TopicId: number;
  TopicName: string;
  TopicTypeId: number;
  TopicWorkflowStageFields: TopicField[];
  DocumentFileHash: string;
  DocumentFileName: string;
  SortOrder: number;
  Source: number;
}

export interface UploadedDocument {
  FileId: number | string;
  FileName: string;
  FileSize: number;
  FileToken: string;
  StorageFileId: number;
  DocumentFileHash?: string;
  DocumentFileName: string;
  SortOrder: number;
  Source: number;
}

export interface SubmissionProfileSiteModel {
  CreatedBy: number;
  CreatedDate: string;
  LockedBy: null;
  ParentId: number;
  SiteId: number;
  TopicFields: TopicField[];
  TopicId: number;
  TopicName: string;
  TopicTypeId: number;
  TopicWorkflowStageFields: any[];
}

export interface SubmissionProfileView {
  EntitiesCount: number;
  ViewFilterDisplayName: string;
  ViewFilterId: number;
}

export interface GetSubmissionProfileViewsOptions {
  siteId: number;
  viewMode: number;
  viewFilter: number | string;
}

export type GetSubmissionProfilesRecord = SimpleTopic & {
  CreatedBy: number;
  CreatedDate: string;
  LockedBy: string | number;
  ParentId: number;
  SiteId: number;
  TopicFields: TopicField[];
  TopicId: number;
  TopicName: string;
  TopicTypeId: number;
  Placeholders: GetPlaceholdersData[];
  LinkedDocuments: LinkedDocument[];
  UploadedDocuments: UploadedDocument[];
  InvestigativeSites: SubmissionProfileSiteModel[];
};

export interface SubmissionLinkDocumentsOptions {
  siteId: number;
  submissionProfileId: number;
  documentIds: number[];
}

export enum PackagesUpdatesActivity {
  CREATE = 309,
  UPDATE = 310,
  DELETE = 311,

  ADD_DOCUMENT = 229,
  DELETE_DOCUMENT = 230,
  MODIFY_DOCUMENT = 312,
  CHANGE_DOCUMENT_ORDER = 313,
  ADD_SITE = 316,
  DELETE_SITE = 317,
  UPDATE_SITE_STATUS = 318,
  ADD_PLACEHOLDER = 371,
  DELETE_PLACEHOLDER = 372,
  UPDATE_PLACEHOLDER = 373,
}

@Injectable()
export class SubmissionProfileService extends ServiceBase {
  serviceUrl = 'SubmissionProfileService.svc';

  getSubmissionProfiles(body: IGetSubmissionProfiles, ...params): Observable<OrigamiResponse<GetSubmissionProfiles>> {
    return this.origamiPost('/GetSubmissionProfiles', body, ...params);
  }

  linkDocuments(body: SubmissionLinkDocumentsOptions): Observable<OrigamiResponse<any>> {
    return this.origamiPost('/LinkDocuments', body, null, null, true);
  }

  createOrUpdateSubmissionProfile(body: CreatedAgencySubmissionPackage): Observable<any> {
    return this.origamiPost(
      '/CreateOrUpdateSubmissionProfile',
      body,
      null,
      [ErrorCode.FINISHED_WITH_MINOR_ERRORS],
      true,
    );
  }

  deleteSubmissionProfile(body): Observable<any> {
    return this.origamiPost('/DeleteSubmissionProfile', body, null, null, true);
  }

  exportDocuments(body): Observable<any> {
    return this.origamiPost('/ExportDocuments', body, null, null, true);
  }

  getSubmissionProfileHistoryRecords(body): Observable<OrigamiResponse<SubmissionProfileHistoryRecord[]>> {
    return this.origamiPost('/GetSubmissionProfileHistoryRecords', body, null, null, true);
  }

  getSubmissionProfile(body: IGetSubmissionProfile): Observable<OrigamiResponse<GetSubmissionProfilesRecord>> {
    return this.origamiPost('/GetSubmissionProfile', body, null, null, true);
  }

  getSubmissionProfileViews(
    body: GetSubmissionProfileViewsOptions,
  ): Observable<OrigamiResponse<SubmissionProfileView[]>> {
    return this.origamiPost('/GetSubmissionProfileViews', body, null, null, true);
  }
}
